import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Search, RotateCcw, Save, BadgeEuro, Users } from "lucide-react";
import { toast } from "sonner";

const CODES = ["13er", "40er", "120er"];
const CODE_LABEL = { "13er": "13er", "40er": "40er", "120er": "120er Display" };

function eur(v) {
  return v == null || v === "" ? "—" : `${(Number(v) || 0).toFixed(2)} €`;
}

export default function B2BPrices() {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({}); // item_id -> { price, bundle_prices:{...} }
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [applying, setApplying] = useState(false);

  const applyToAllB2B = async () => {
    if (!window.confirm("Globale B2B-Preise an ALLE B2B-Kunden vergeben?\n\nBereits hinterlegte individuelle Kundenpreise bleiben unverändert – es werden nur fehlende Artikel ergänzt.")) return;
    setApplying(true);
    try {
      const { data } = await api.post("/b2b-prices/apply-to-all-b2b");
      toast.success(`Vergeben: ${data.created} neue Preise für ${data.customers} B2B-Kunden (${data.skipped_existing} vorhandene behalten).`);
    } catch (e) { toast.error(e.response?.data?.detail || "Sammelvergabe fehlgeschlagen"); }
    finally { setApplying(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/b2b-prices");
      setRows(data || []);
      setDraft({});
    } catch (_e) { toast.error("B2B-Preise konnten nicht geladen werden"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => (r.name || "").toLowerCase().includes(s) || (r.sku || "").toLowerCase().includes(s));
  }, [rows, q]);

  const cellValue = (r, field, code) => {
    const d = draft[r.item_id];
    if (field === "price") {
      if (d && "price" in d) return d.price;
      return r.price != null ? String(r.price) : "";
    }
    if (d && d.bundle_prices && code in d.bundle_prices) return d.bundle_prices[code];
    const v = (r.bundle_prices || {})[code];
    return v != null ? String(v) : "";
  };

  const setCell = (r, field, code, val) => {
    setDraft((prev) => {
      const cur = prev[r.item_id] ? { ...prev[r.item_id] } : {};
      if (field === "price") cur.price = val;
      else cur.bundle_prices = { ...(cur.bundle_prices || {}), [code]: val };
      return { ...prev, [r.item_id]: cur };
    });
  };

  const isDirty = (r) => !!draft[r.item_id];

  const save = async (r) => {
    setSavingId(r.item_id);
    try {
      const payload = {
        price: (() => { const v = cellValue(r, "price"); return (v === "" || Number(v) === Number(r.price_auto)) ? null : Number(v); })(),
        bundle_prices: Object.fromEntries(CODES.map((c) => {
          const v = cellValue(r, "bundle", c);
          const auto = (r.bundle_auto || {})[c];
          return [c, (v === "" || (auto != null && Number(v) === Number(auto))) ? null : Number(v)];
        })),
      };
      const { data } = await api.put(`/b2b-prices/${r.item_id}`, payload);
      setRows((cur) => cur.map((x) => (x.item_id === r.item_id ? data : x)));
      setDraft((prev) => { const n = { ...prev }; delete n[r.item_id]; return n; });
      toast.success(`${r.name}: B2B-Preise gespeichert`);
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSavingId(null); }
  };

  const reset = async (r) => {
    setSavingId(r.item_id);
    try {
      const { data } = await api.delete(`/b2b-prices/${r.item_id}`);
      setRows((cur) => cur.map((x) => (x.item_id === r.item_id ? data : x)));
      setDraft((prev) => { const n = { ...prev }; delete n[r.item_id]; return n; });
      toast.success(`${r.name}: auf automatische Berechnung zurückgesetzt`);
    } catch (e) { toast.error(e.response?.data?.detail || "Zurücksetzen fehlgeschlagen"); }
    finally { setSavingId(null); }
  };

  return (
    <div data-testid="page-b2b-prices">
      <PageHeader
        eyebrow="Verkauf"
        title="B2B-Preise"
        description="Globale Preismatrix für B2B-Kunden. Vorbefüllt aus Einzelpreis × Einheiten, pro Zelle überschreibbar. Dient als Vorbelegung – individuelle Kundenpreise haben Vorrang."
      />

      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Artikel oder SKU suchen…"
                 data-testid="b2b-search" className="pl-9" />
        </div>
        <Button onClick={applyToAllB2B} disabled={applying}
                data-testid="b2b-apply-all-btn"
                className="rounded-full bg-[var(--pop-pink)] text-white font-bold sm:ml-auto">
          <Users size={15} className="mr-2" />
          {applying ? "Vergebe…" : "An alle B2B-Kunden vergeben"}
        </Button>
      </div>

      {loading ? (
        <div className="text-[var(--text-muted)] py-16 text-center">Lade…</div>
      ) : (
        <Card className="rounded-2xl border border-[var(--border)] overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="overline">Artikel</TableHead>
                <TableHead className="overline text-right">Einzel (€)</TableHead>
                {CODES.map((c) => <TableHead key={c} className="overline text-right">{CODE_LABEL[c]} (€)</TableHead>)}
                <TableHead className="overline text-right">Aktion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.item_id} data-testid={`b2b-row-${r.sku}`}>
                  <TableCell className="min-w-[200px]">
                    <div className="font-semibold">{r.name}
                      {r.has_override && <span className="ml-2 pop-pill bg-[var(--pop-cream)] text-[10px]" data-testid={`b2b-override-${r.sku}`}>angepasst</span>}
                    </div>
                    <div className="font-mono text-xs text-[var(--text-muted)]">{r.sku}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Input type="number" step="0.01" min="0" value={cellValue(r, "price")}
                           data-testid={`b2b-price-${r.sku}-single`}
                           onChange={(e) => setCell(r, "price", null, e.target.value)}
                           className="w-24 ml-auto text-right font-mono" />
                    <div className="text-[10px] text-[var(--text-muted)] mt-0.5">Auto {eur(r.price_auto)}</div>
                  </TableCell>
                  {CODES.map((c) => (
                    <TableCell key={c} className="text-right">
                      <Input type="number" step="0.01" min="0" value={cellValue(r, "bundle", c)}
                             data-testid={`b2b-price-${r.sku}-${c}`}
                             onChange={(e) => setCell(r, "bundle", c, e.target.value)}
                             className="w-24 ml-auto text-right font-mono" />
                      <div className="text-[10px] text-[var(--text-muted)] mt-0.5">Auto {eur((r.bundle_auto || {})[c])}</div>
                    </TableCell>
                  ))}
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" onClick={() => save(r)} disabled={savingId === r.item_id || !isDirty(r)}
                            data-testid={`b2b-save-${r.sku}`}
                            className="rounded-full bg-[var(--pop-pink)] text-white font-bold mr-2">
                      <Save size={13} className="mr-1" /> Speichern
                    </Button>
                    <button onClick={() => reset(r)} disabled={savingId === r.item_id || !r.has_override}
                            data-testid={`b2b-reset-${r.sku}`}
                            className="text-xs text-[var(--text-muted)] hover:text-[var(--pop-pink)] disabled:opacity-30 inline-flex items-center gap-1">
                      <RotateCcw size={12} /> Auto
                    </button>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={CODES.length + 3} className="text-center py-10 text-[var(--text-muted)]">
                  <BadgeEuro size={28} className="mx-auto mb-2 opacity-40" />
                  Keine Artikel gefunden.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
